/**
 * Admin Overview Page
 *
 * Main dashboard for admin with key metrics and alerts
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAdminStore, useAdminStats, useAdminLoading } from '@/stores/adminStore';
import { ROUTES } from '@/constants';
import {
  Users,
  TrendingUp,
  Coins,
  AlertTriangle,
  CreditCard,
  Activity,
  ArrowRight,
  Loader2,
} from 'lucide-react';

export function AdminOverviewPage() {
  const { fetchAdminStats, fetchAllCompanies } = useAdminStore();
  const stats = useAdminStats();
  const isLoading = useAdminLoading();

  useEffect(() => {
    Logger.info(LogCategory.UI, 'AdminOverviewPage mounted', 'AdminOverviewPage');
    const loadData = async () => {
      await fetchAllCompanies();
      await fetchAdminStats();
    };
    loadData();
  }, [fetchAdminStats, fetchAllCompanies]);

  if (isLoading || !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Admin Overview</h2>
          <p className="text-slate-500 mt-1">Monitor your SaaS business at a glance</p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Customers */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Accounts</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.totalCustomers}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {stats.externalCustomers} external, {stats.internalAccounts} internal
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credits Issued */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Credits</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.totalCreditsIssued.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Avg: {stats.avgCreditsPerCustomer}/customer (external only)
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Coins className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Low Credits Alert */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Low on Credits</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.customersLowOnCredits}
                  </p>
                  <p className="text-sm text-warning-600 mt-1">
                    Need attention
                  </p>
                </div>
                <div className="h-12 w-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-warning-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Failed Billing */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Failed Billing</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {stats.failedBillingCount}
                  </p>
                  <p className="text-sm text-danger-600 mt-1">
                    Requires action
                  </p>
                </div>
                <div className="h-12 w-12 bg-danger-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-danger-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {(stats.customersLowOnCredits > 0 || stats.failedBillingCount > 0) && (
          <Card>
            <CardHeader
              title="Alerts & Notifications"
              description="Issues requiring immediate attention"
            />
            <CardContent>
              <div className="space-y-3">
                {stats.customersLowOnCredits > 0 && (
                  <div className="flex items-start gap-3 p-4 bg-warning-50 border border-warning-200 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-warning-900">
                        {stats.customersLowOnCredits} customer{stats.customersLowOnCredits !== 1 ? 's' : ''} running low on credits
                      </p>
                      <p className="text-sm text-warning-700 mt-1">
                        These customers may need credit top-ups or plan upgrades
                      </p>
                    </div>
                    <Link to={ROUTES.ADMIN_CUSTOMERS}>
                      <Button variant="outline" size="sm">
                        View Customers
                      </Button>
                    </Link>
                  </div>
                )}

                {stats.failedBillingCount > 0 && (
                  <div className="flex items-start gap-3 p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <CreditCard className="h-5 w-5 text-danger-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-danger-900">
                        {stats.failedBillingCount} failed billing transaction{stats.failedBillingCount !== 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-danger-700 mt-1">
                        Payment failures need to be resolved to maintain service
                      </p>
                    </div>
                    <Link to={ROUTES.ADMIN_CUSTOMERS}>
                      <Button variant="outline" size="sm">
                        Review Issues
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Manage Customers</h3>
                  <p className="text-sm text-slate-500">View and edit customer accounts</p>
                </div>
              </div>
              <Link to={ROUTES.ADMIN_CUSTOMERS}>
                <Button variant="outline" className="w-full">
                  Go to Customers
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">View Analytics</h3>
                  <p className="text-sm text-slate-500">Credit usage and API statistics</p>
                </div>
              </div>
              <Link to={ROUTES.ADMIN_ANALYTICS}>
                <Button variant="outline" className="w-full">
                  View Analytics
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Promotions</h3>
                  <p className="text-sm text-slate-500">Create and manage promo codes</p>
                </div>
              </div>
              <Link to={ROUTES.ADMIN_PROMOTIONS}>
                <Button variant="outline" className="w-full">
                  Manage Promos
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
